import React, { useEffect, useRef, useState } from 'react';
import { useUnity } from '../../contexts/UnityContext';
import type { UnityBridgeEvent, UnityBridgeGlobals, UnityInstanceLike, UnityLoadConfig } from '../../types/unity';

interface UnityViewerProps {
  pieceId?: string;
  onUnityReady?: (unityInstance: UnityInstanceLike) => void;
  onUnityError?: (error: string) => void;
  hidden?: boolean;
}

declare global {
  interface Window {
    createUnityInstance?: (
      canvas: HTMLCanvasElement,
      config: UnityLoadConfig,
      progressCallback: (progress: number) => void
    ) => Promise<UnityInstanceLike>;
    __unityInstance?: UnityInstanceLike;
    __unityInitPromise?: Promise<UnityInstanceLike>;
    __artvrUnityBridge?: UnityBridgeGlobals;
    onUnityEvent?: (payload: string | UnityBridgeEvent) => void;
    OnUnityEvent?: (payload: string | UnityBridgeEvent) => void;
    UnityPostMessageToWeb?: (payload: string | UnityBridgeEvent) => void;
  }
}

const unityEnv = import.meta.env as Record<string, string | undefined>;
const DEFAULT_UNITY_BASE_URL = 'https://pub-3c7e45a0dd69437392cceb0f37fb6241.r2.dev/unity';
const DEFAULT_UNITY_LOADER_PREFIX = 'webgl-build';
const DEFAULT_UNITY_BUILD_VERSION = '202606211950';

const getUnityBaseUrl = () => unityEnv.VITE_UNITY_BASE_URL || DEFAULT_UNITY_BASE_URL;
const getUnityLoaderPrefix = () => unityEnv.VITE_UNITY_LOADER_PREFIX || DEFAULT_UNITY_LOADER_PREFIX;
const getUnityCacheBust = () => `?v=${unityEnv.VITE_UNITY_BUILD_VERSION || DEFAULT_UNITY_BUILD_VERSION}`;
const getUnityMaxDpr = () => Math.max(1, Number(unityEnv.VITE_UNITY_MAX_DPR || 2));
const getDeviceDpr = () => (typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1);
const getVisibleMaxDimension = () => Number(unityEnv.VITE_UNITY_MAX_DIM_VISIBLE || 4096);
const getHiddenMaxDimension = () => Number(unityEnv.VITE_UNITY_MAX_DIM_HIDDEN || 2048);
const getUnityDpr = (hidden?: boolean) => {
  const targetDpr = Math.min(getDeviceDpr(), getUnityMaxDpr());
  return hidden ? Math.min(targetDpr, Number(unityEnv.VITE_UNITY_HIDDEN_DPR || 1.25)) : targetDpr;
};

// Inject Unity Template CSS once so stock loader styles are applied
const ensureUnityTemplateStyles = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById('unity-template-style')) return;
  const UNITY_BASE_URL = getUnityBaseUrl();
  const cacheBust = getUnityCacheBust();
  const link = document.createElement('link');
  link.id = 'unity-template-style';
  link.rel = 'stylesheet';
  link.href = `${UNITY_BASE_URL}/TemplateData/style.css${cacheBust}`;
  document.head.appendChild(link);
};

const getCanvasPixelSize = (container: HTMLElement, hidden?: boolean) => {
  const rect = container.getBoundingClientRect();
  const cssWidth = Math.max(1, rect.width || container.clientWidth || window.innerWidth || 1);
  const cssHeight = Math.max(1, rect.height || container.clientHeight || window.innerHeight || 1);
  const dpr = getUnityDpr(hidden);
  const maxDimension = hidden ? getHiddenMaxDimension() : getVisibleMaxDimension();

  return {
    dpr,
    width: Math.max(1, Math.min(maxDimension, Math.round(cssWidth * dpr))),
    height: Math.max(1, Math.min(maxDimension, Math.round(cssHeight * dpr))),
  };
};

const applyUnityCanvasSize = (
  container: HTMLElement,
  canvas: HTMLCanvasElement,
  unityInstance?: UnityInstanceLike | null,
  hidden?: boolean
) => {
  const size = getCanvasPixelSize(container, hidden);

  canvas.style.width = '100%';
  canvas.style.height = '100%';

  if (canvas.width !== size.width) {
    canvas.width = size.width;
  }
  if (canvas.height !== size.height) {
    canvas.height = size.height;
  }

  const unityCanvas = unityInstance?.Module?.canvas;
  if (unityCanvas && unityCanvas !== canvas) {
    unityCanvas.style.width = '100%';
    unityCanvas.style.height = '100%';
    if (unityCanvas.width !== size.width) {
      unityCanvas.width = size.width;
    }
    if (unityCanvas.height !== size.height) {
      unityCanvas.height = size.height;
    }
  }

  unityInstance?.Module?.setCanvasSize?.(size.width, size.height);
  return size;
};

export default function UnityViewer({ onUnityReady, onUnityError, hidden }: UnityViewerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [unityInstance, setUnityInstance] = useState<UnityInstanceLike | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const initializedRef = useRef(false);
  const dprRef = useRef<number>(typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1);
  const progressFrameRef = useRef<number | null>(null);
  const pendingProgressRef = useRef(0);
  const { onUnityReady: ctxOnUnityReady, onUnityError: ctxOnUnityError, onUnityEvent: ctxOnUnityEvent, onUnityProgress } = useUnity();

  const publishProgress = React.useCallback((progress: number, immediate = false) => {
    const clamped = Math.max(0, Math.min(1, progress));
    pendingProgressRef.current = clamped;

    if (immediate || progressFrameRef.current === null) {
      const emit = () => {
        progressFrameRef.current = null;
        const nextProgress = pendingProgressRef.current;
        setLoadingProgress(nextProgress);
        onUnityProgress(nextProgress);
      };

      if (immediate || typeof requestAnimationFrame === 'undefined') {
        emit();
      } else {
        progressFrameRef.current = requestAnimationFrame(emit);
      }
    }
  }, [onUnityProgress]);

  useEffect(() => {
    const eventHandler = (payload: string | UnityBridgeEvent) => {
      ctxOnUnityEvent(payload);
    };

    window.__artvrUnityBridge = { onUnityEvent: eventHandler };
    window.onUnityEvent = eventHandler;
    window.OnUnityEvent = eventHandler;
    window.UnityPostMessageToWeb = eventHandler;

    return () => {
      if (progressFrameRef.current !== null) {
        cancelAnimationFrame(progressFrameRef.current);
        progressFrameRef.current = null;
      }
      if (window.__artvrUnityBridge?.onUnityEvent === eventHandler) {
        delete window.__artvrUnityBridge;
      }
      if (window.onUnityEvent === eventHandler) {
        delete window.onUnityEvent;
      }
      if (window.OnUnityEvent === eventHandler) {
        delete window.OnUnityEvent;
      }
      if (window.UnityPostMessageToWeb === eventHandler) {
        delete window.UnityPostMessageToWeb;
      }
    };
  }, [ctxOnUnityEvent]);

  useEffect(() => {
    if (!canvasRef.current || initializedRef.current) return;
    initializedRef.current = true;
    ensureUnityTemplateStyles();

    // If a global instance already exists, reuse it without re-initializing
    if (window.__unityInstance) {
      setUnityInstance(window.__unityInstance);
      setIsLoading(false);
      setLoadingProgress(1);
      publishProgress(1, true);
      onUnityReady?.(window.__unityInstance);
      ctxOnUnityReady(window.__unityInstance);
      ctxOnUnityEvent({ name: 'Runtime.Ready' });
      return;
    }

    // If an initialization is already in-flight, attach to it
    if (window.__unityInitPromise) {
      window.__unityInitPromise
        .then((inst) => {
          setUnityInstance(inst);
          setIsLoading(false);
          setLoadingProgress(1);
          publishProgress(1, true);
          onUnityReady?.(inst);
          ctxOnUnityReady(inst);
          ctxOnUnityEvent({ name: 'Runtime.Ready' });
        })
        .catch((err) => {
          const errorMessage = err instanceof Error ? err.message : 'Failed to initialize Unity';
          setError(errorMessage);
          setIsLoading(false);
          onUnityError?.(errorMessage);
          ctxOnUnityError(errorMessage);
        });
      return;
    }

    const initializeUnity = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Load Unity loader script and capture chosen prefix/base URL
        const envBase = getUnityBaseUrl();
        const envPrefix = getUnityLoaderPrefix();
        let loaderInfo: { baseUrl: string; prefix: string };
        if (!window.createUnityInstance) {
          loaderInfo = await loadUnityLoader();
        } else {
          // Loader already present; fall back to env-derived info
          loaderInfo = { baseUrl: envBase, prefix: envPrefix };
        }

        // Unity configuration
        // Resolve Unity base URL from env, defaulting to local public/unity
        const UNITY_BASE_URL = loaderInfo.baseUrl;
        const filePrefix = loaderInfo.prefix || 'unity';
        const cacheBust = getUnityCacheBust();
        const desiredDpr = getUnityDpr(hidden);
        const container = containerRef.current;
        const canvas = canvasRef.current;

        if (container && canvas) {
          dprRef.current = applyUnityCanvasSize(container, canvas, null, hidden).dpr;
        }

        const config: UnityLoadConfig = {
          dataUrl: `${UNITY_BASE_URL}/Build/${filePrefix}.data${cacheBust}`,
          frameworkUrl: `${UNITY_BASE_URL}/Build/${filePrefix}.framework.js${cacheBust}`,
          codeUrl: `${UNITY_BASE_URL}/Build/${filePrefix}.wasm${cacheBust}`,
          streamingAssetsUrl: `${UNITY_BASE_URL}/StreamingAssets`,
          companyName: "ArtVR LLC",
          productName: "ArtVR",
          productVersion: "5.0b1",
          // We manage render size explicitly to match the CSS canvas using ResizeObserver
          matchWebGLToCanvasSize: false,
          // Start with a conservative DPR to reduce main-thread/GPU spikes during init.
          // We'll ramp up to the actual DPR after Unity is ready.
          devicePixelRatio: desiredDpr,
          webglContextAttributes: { antialias: false, preserveDrawingBuffer: false, alpha: false, depth: true, stencil: false, powerPreference: 'low-power', desynchronized: true },
          showBanner: (msg: string, type: string) => {
            console.log(`Unity ${type}:`, msg);
            if (type === 'error') {
              onUnityError?.(msg);
            }
          },
        };

        // Create Unity instance once and cache it globally to prevent reloads
        window.__unityInitPromise = window
          .createUnityInstance!(
            canvasRef.current!,
            config,
            (progress: number) => publishProgress(progress)
          )
          .then((inst) => {
            window.__unityInstance = inst;
            return inst;
          });

        const instance = await window.__unityInitPromise;

        setUnityInstance(instance);
        setIsLoading(false);
        setLoadingProgress(1);
        publishProgress(1, true);
        onUnityReady?.(instance);
        ctxOnUnityReady(instance);
        ctxOnUnityEvent({ name: 'Runtime.Ready' });

        // Resize once after Unity has attached its Module canvas.
        requestAnimationFrame(() => {
          const currentContainer = containerRef.current;
          const currentCanvas = canvasRef.current;
          if (!currentContainer || !currentCanvas) return;
          dprRef.current = applyUnityCanvasSize(currentContainer, currentCanvas, instance, hidden).dpr;
        });

        console.log('Unity instance created successfully');
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to initialize Unity';
        setError(errorMessage);
        setIsLoading(false);
        onUnityError?.(errorMessage);
        ctxOnUnityError(errorMessage);
        console.error('Failed to initialize Unity:', err);
      }
    };

    // Load Unity immediately when component mounts (only once)
    initializeUnity();

    // Do not quit Unity on unmount; keep the single runtime alive for the app lifetime
    return () => {};
  }, [ctxOnUnityError, ctxOnUnityEvent, ctxOnUnityReady, hidden, onUnityError, publishProgress, onUnityReady]);

  // If Unity was initialized while hidden (2D mode), increase resolution when revealed
  useEffect(() => {
    if (!unityInstance) return;
    if (hidden) return;
    const container = (document.getElementById('unity-react-container') as HTMLDivElement) || null;
    const canvas = (document.getElementById('unity-canvas') as HTMLCanvasElement) || null;
    if (!container || !canvas) return;
    try {
      const inst = window.__unityInstance || unityInstance;
      dprRef.current = applyUnityCanvasSize(container, canvas, inst, false).dpr;
    } catch {
      // ignore
    }
  }, [hidden, unityInstance]);

  const loadUnityLoader = (): Promise<{ baseUrl: string; prefix: string }> => {
    return new Promise((resolve, reject) => {
      const existing = document.getElementById('unity-loader-script') as HTMLScriptElement | null;
      const envBase = getUnityBaseUrl();
      const envPrefix = getUnityLoaderPrefix();
      const cacheBust = getUnityCacheBust();

      if (existing) {
        // If the script already exists, resolve with env-derived info (we cannot infer the prefix reliably)
        if (typeof window.createUnityInstance === 'function') {
          return resolve({ baseUrl: envBase, prefix: envPrefix });
        } else {
          existing.addEventListener('load', () => {
            if (typeof window.createUnityInstance === 'function') {
              resolve({ baseUrl: envBase, prefix: envPrefix });
            } else {
              reject(new Error('Unity loader loaded, but createUnityInstance was not found (likely an HTML fallback was served).'));
            }
          });
          existing.addEventListener('error', () => reject(new Error('Failed to load Unity loader script')));
        }
        return;
      }

      const UNITY_BASE_URL = envBase;
      const preferred = envPrefix;
      const candidates = [
        `${UNITY_BASE_URL}/Build/${preferred}.loader.js${cacheBust}`,
        `${UNITY_BASE_URL}/Build/webgl-build.loader.js${cacheBust}`,
        `${UNITY_BASE_URL}/Build/unity.loader.js${cacheBust}`,
        `${UNITY_BASE_URL}/Build/hosting.loader.js${cacheBust}`,
      ];

      const tryNext = (idx: number) => {
        if (idx >= candidates.length) {
          reject(new Error(`Failed to load Unity loader script from: ${candidates.join(' , ')}`));
          return;
        }
        const url = candidates[idx];
        const script = document.createElement('script');
        script.id = 'unity-loader-script';
        script.src = url;
        script.async = true;
        script.onload = () => {
          if (typeof window.createUnityInstance === 'function') {
            const m = url.match(/\/Build\/([^/]+)\.loader\.js$/);
            const prefix = m ? m[1] : envPrefix;
            resolve({ baseUrl: UNITY_BASE_URL, prefix });
          } else {
            console.warn(`Unity loader at ${url} loaded but createUnityInstance undefined. Trying next candidate...`);
            // Clean up this script tag before trying next
            document.head.removeChild(script);
            tryNext(idx + 1);
          }
        };
        script.onerror = () => {
          console.warn(`Failed to load Unity loader at ${url}`);
          // Clean up and try next candidate
          try {
            document.head.removeChild(script);
          } catch (error) {
            console.debug('Unity loader cleanup skipped:', error);
          }
          tryNext(idx + 1);
        };
        document.head.appendChild(script);
      };

      tryNext(0);
    });
  };

  // Keep Unity render size in sync with container using device pixel ratio
  useEffect(() => {
    const updateCanvasSize = () => {
      const container = containerRef.current;
      const canvas = canvasRef.current;
      if (!container || !canvas) return;

      try {
        const inst = window.__unityInstance || unityInstance;
        dprRef.current = applyUnityCanvasSize(container, canvas, inst, hidden).dpr;
      } catch {
        // no-op; will retry on next resize/ready
      }
    };

    updateCanvasSize();

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => updateCanvasSize());
      if (containerRef.current) {
        ro.observe(containerRef.current);
      }
    }

    // Also listen to window resize/orientation/DPR changes
    window.addEventListener('resize', updateCanvasSize);

    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', updateCanvasSize);
    };
  }, [unityInstance, hidden]);

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-900">
        <div className="text-center text-white">
          <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium mb-2">Unity Viewer Error</h3>
          <p className="text-gray-400 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} id="unity-react-container" className="w-full h-full relative bg-black overflow-hidden">
      {/* Unity Canvas */}
      <canvas
        ref={canvasRef}
        id="unity-canvas"
        className="w-full h-full"
        style={{ width: '100%', height: '100%', display: 'block' }}
        tabIndex={-1}
      />
      <div id="unity-loading-bar" style={{ display: isLoading ? 'block' : 'none' }}>
        <div id="unity-logo"></div>
        <div id="unity-progress-bar-empty">
          <div id="unity-progress-bar-full" style={{ width: `${Math.round(loadingProgress * 100)}%` }}></div>
        </div>
      </div>

    </div>
  );
}
